import {Injectable} from '@angular/core';
import {AbstractControl, FormGroup, NonNullableFormBuilder, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {Observable, take} from 'rxjs';
import {ProjectTask, Subtask, TaskTemplate, TaskTrigger} from 'src/app/core/model/task';
import {Tracker} from 'src/app/core/model/tracker';
import {selectTrackers} from 'src/app/core/store/trackers/trackers.selector';
import {REWARD_COST_MAX, REWARD_COST_MIN} from 'src/app/rewards/shared/rewards-constants';
import {minMaxValidator} from 'src/app/rewards/shared/rewards-dialog/min-max-validator';
import {TimezoneService} from 'src/app/services/timezone.service';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import * as uuid from 'uuid';
import {TASK_ICON_PICKER_ICONS} from '../tasks/icon-picker/task-icon-picker-icons';
import {TaskRepeatTimeValidityData} from '../tasks/model/task-repeat-time-validity-data';
import {formatSelectedDay} from '../tasks/utils/format-selected-day';
@Injectable({
  providedIn: 'root',
})
export class TasksService {
  constructor(
    private formBuilder: NonNullableFormBuilder,
    private timezoneService: TimezoneService,
    private store$: Store
  ) {}

  trackers$: Observable<Tracker[]> = this.store$.pipe(select(selectTrackers));
  today = formatSelectedDay(new Date());
  subtaskEditIndex: number;
  editingSubtask: boolean;
  defaultSchedule = {
    repeatType: 'weeks',
    repeatEvery: 1,
    repeatDays: [],
    repeatEndsIn: 'onRepeat',
    scheduleType: 'doNotRepeat',
    scheduleOnDateType: 'asap',
    scheduleOnDate: '',
    cycleEndsType: 'endOfProject',
    cycleEndsTimeValue: 1,
    cycleEndsTimeValueType: 'weeks',
    cycleEndsTaskCountValue: 1,
    cycleEndsTrigger: null,
    expirationType: 'endOfProject',
    expirationDate: '',
    expirationValue: 1,
    expirationValueType: 'hours',
  };

  getTaskTemplateForm(task?: TaskTemplate): FormGroup {
    return this.formBuilder.group({
      label: [task?.label || '', Validators.required],
      icon: [task?.icon || 'alarm-clock'],
      duration: [task?.duration || 15],
      subtasks: this.formBuilder.array(
        task?.subtasks ? task.subtasks.map(subtask => this.getSubtaskFormGroup(subtask)) : []
      ),
    });
  }

  getTaskForm(task?: ProjectTask, template?: boolean, defaultCoinId?: string, projectUsers?: string[]): FormGroup {
    if (template) {
      return this.getTaskTemplateForm(task);
    }

    return this.formBuilder.group({
      autoReset: [task?.autoReset || false],
      label: [task?.label || '', Validators.required],
      icon: [task?.icon || 'alarm-clock'],
      duration: [task?.duration || 15],
      priority: [task?.priority || 2],
      schedule: this.formBuilder.group({
        timezone: [this.timezoneService.getTimeZone()],
        repeatType: [task?.schedule?.repeatType || 'weeks'], // weeks, days
        repeatEvery: [task?.schedule?.repeatEvery || 1],
        repeatDays: [task?.schedule?.repeatDays || []],
        repeatEndsIn: [task?.schedule?.repeatEndsIn || 'onRepeat'], // onRepeat, 1 hour, 2 hours, etc.
        scheduleType: [task?.schedule?.scheduleType || 'doNotRepeat'], // doNotRepeat, repeats, trigger
        scheduleOnDateType: [task?.schedule?.scheduleOnDateType || 'asap'], // asap or date
        scheduleOnDate: [
          task?.schedule?.scheduleOnDate
            ? this.timezoneService.convertDateToUtcString(
                moment(task?.schedule?.scheduleOnDate)
                  .tz(this.timezoneService.getTimeZone())
                  .toDate()
              )
            : this.timezoneService.convertDateToUtcString(
                moment().tz(this.timezoneService.getTimeZone()).startOf('day').toDate()
              ),
        ], // UTC date string
        cycleEndsType: [task?.schedule?.cycleEndsType || 'endOfProject'],
        cycleEndsTimeValue: [task?.schedule?.cycleEndsTimeValue || 1],
        cycleEndsTimeValueType: [task?.schedule?.cycleEndsTimeValueType || 'weeks'],
        cycleEndsTaskCountValue: [task?.schedule?.cycleEndsTaskCountValue || 1],
        cycleEndsTrigger: this.getTriggerFormGroup(task?.schedule?.cycleEndsTrigger),
        expirationType: [task?.schedule?.expirationType || 'endOfProject'],
        expirationDate: this.getExpirationDate(task),
        expirationValue: [task?.schedule?.expirationValue || 1],
        expirationValueType: [task?.schedule?.expirationValueType || 'hours'],
      }),
      triggers: this.formBuilder.array(
        task?.triggers ? task.triggers.map(trigger => this.getTriggerFormGroup(trigger)) : []
      ),
      triggerEndsType: [task?.triggerEndsType || 'forever'],
      triggerEndsTimeValue: [task?.triggerEndsTimeValue || 1],
      triggerEndsTimeValueType: [task?.triggerEndsTimeValueType || 'weeks'],
      triggerEndsTaskCountValue: [task?.triggerEndsTaskCountValue || 1],
      triggerDisplayAlert: [task?.triggerDisplayAlert || false],
      triggeredTaskExpiration: [task?.triggeredTaskExpiration || 'never'],
      subtasks: this.formBuilder.array(
        task?.subtasks ? task.subtasks.map(subtask => this.getSubtaskFormGroup(subtask)) : []
      ),
      teams: [task?.teams || []],
      users: [task?.users || projectUsers || []],
      skills: [task?.skills || []],
      taskCoinType: [task?.taskCoinType || defaultCoinId || ''],
      taskCoinValue: [task?.taskCoinValue || 0, [minMaxValidator(REWARD_COST_MIN, REWARD_COST_MAX)]],
      deleteWhenComplete: [task?.deleteWhenComplete || false],
    });
  }

  getExpirationDate(task?: ProjectTask): string {
    const scheduleOnMoment = moment(task?.schedule?.scheduleOnDate).tz(this.timezoneService.getTimeZone());
    const expirationMoment = moment(task?.schedule?.expirationDate).tz(this.timezoneService.getTimeZone());

    // if the schedule on date is after the expiration date, return the schedule on date
    if (scheduleOnMoment.isAfter(expirationMoment)) {
      return this.timezoneService.convertDateToUtcString(scheduleOnMoment.endOf('day').toDate());
    }

    // if the expiration date is after the schedule on date, return the expiration date
    if (expirationMoment.isAfter(scheduleOnMoment)) {
      return this.timezoneService.convertDateToUtcString(expirationMoment.endOf('day').toDate());
    }

    return this.timezoneService.convertDateToUtcString(
      moment().tz(this.timezoneService.getTimeZone()).endOf('day').toDate()
    );
  }

  getTriggerFormGroup(trigger?: TaskTrigger): AbstractControl {
    return this.formBuilder.group({
      type: [trigger?.type || 'metric'],
      id: [trigger?.id || uuid.v4()],
      taskInstanceId: [trigger?.taskInstanceId || ''],
      metricId: [trigger?.metricId || ''],
      trackerId: [trigger?.trackerId || ''],
      operator: [trigger?.operator || 'eq'],
      value: [trigger?.value || ''],
    });
  }

  getSubtaskFormGroup(subtask?: Subtask): AbstractControl {
    return this.formBuilder.group({
      _id: [subtask?._id || uuid.v4()],
      complete: [subtask?.complete || false],
      trackerId: [subtask?.trackerId || ''],
      metricIds: [this.getMetricIds(subtask?.metricIds, subtask?.trackerId) || []],
      body: [subtask?.body || ''],
      title: [subtask?.title || ''],
    });
  }

  getMetricIds(
    metricIds: {metricId: string; readOnly: boolean}[] | string[],
    trackerId: string
  ): {metricId: string; readOnly: boolean}[] {
    if (!metricIds) return [];
    const metricIdsArray = [];
    let tracker: Tracker;
    if (trackerId) {
      this.trackers$.pipe(take(1)).subscribe((trackers: Tracker[]) => {
        tracker = trackers.find(t => t._id === trackerId);
      });
    }

    for (const metricId of metricIds) {
      if (typeof metricId === 'string') {
        metricIdsArray.push({metricId, readOnly: false});
      } else {
        if (tracker && tracker.metrics.find(m => m._id === metricId.metricId)) {
          metricIdsArray.push(metricId);
        }
      }
    }
    return metricIdsArray;
  }

  setSubtaskEditIndex(index: number, editing?: boolean) {
    this.subtaskEditIndex = index;
    this.editingSubtask = editing || false;
  }

  getSubtaskEditIndex(): number {
    return this.subtaskEditIndex;
  }

  getEditingSubtask(): boolean {
    return this.editingSubtask;
  }

  formatProjectTaskFromForm(form: FormGroup): ProjectTask {
    const subtasks = form.get('subtasks').value;
    const triggers = form.get('triggers').value;
    const label = form.get('label').value;
    const taskSchedule = form.get('schedule').value;
    const cycleEndsTrigger = taskSchedule.cycleEndsTrigger;
    const task = {
      ...form.value,
      schedule: {
        ...taskSchedule,
        cycleEndsTrigger: taskSchedule.cycleEndsType === 'trigger' ? cycleEndsTrigger : {},
      },
      subtasks: subtasks.length === 0 ? this.addSubtask(label) : subtasks,
      triggers:
        taskSchedule.scheduleType === 'trigger'
          ? triggers.filter((trigger: TaskTrigger) => this.checkIfTriggerIsComplete(trigger))
          : [],
    };
    if (task.taskCoinValue === 0) {
      delete task.taskCoinType;
      delete task.taskCoinValue;
    }
    return task;
  }

  formatTaskTemplateFromForm(form: FormGroup): TaskTemplate {
    const subtasks = form.get('subtasks').value;
    const label = form.get('label').value;
    const task = {
      ...form.value,
      subtasks: subtasks.length === 0 ? this.addSubtask(label) : subtasks,
    };
    // Templates do not have triggers or schedule - this is initilizing them if the template is created from a task
    task.triggers = [];
    task.schedule = this.defaultSchedule;
    task.event = false;
    return task;
  }

  addSubtask(label: string): Subtask[] {
    return [{_id: uuid.v4(), complete: false, body: '', title: label, trackerId: '', metricIds: [], notes: []}];
  }

  // checks if task expiration time is less than task repetition time, everything is converted to hours for comparison
  getRepeatTimeValidityData(
    scheduleControls: AbstractControl,
    valueType: string,
    value: number
  ): TaskRepeatTimeValidityData {
    const {repeatType, repeatEvery, repeatDays} = scheduleControls.value;
    const multiplier = this.getMultiplier(valueType);
    const totalTime = multiplier * value;

    if (repeatDays?.length === 0) {
      return {valid: true};
    }

    if (repeatType === 'day') {
      const timeBeforeNextTask = repeatEvery * 24;
      return {
        valid: timeBeforeNextTask >= totalTime,
        timeBeforeNextTask,
        timeBeforeNextTaskDisplay: this.formatDuration(timeBeforeNextTask),
        totalTime,
      };
    }
    // everything following assumes repeatType === 'week'
    let timeBeforeNextTask: number;
    if (repeatDays.length === 1) {
      // if there is only one day selected, hours in week * how many weeks
      timeBeforeNextTask = 24 * 7 * repeatEvery;
    }
    if (repeatDays.length > 1) {
      // if multiple days are selected, time between tasks + (how many weeks * hours in week)
      if (repeatEvery === 1) {
        const dayDifferenceBetweenRepeatingTasks = this.findSmallestDayDifferenceWithWraparound(repeatDays);
        timeBeforeNextTask = dayDifferenceBetweenRepeatingTasks * 24;
      }
      if (repeatEvery > 1) {
        const dayDifferenceBetweenRepeatingTasks = this.findSmallestDayDifference(repeatDays);
        timeBeforeNextTask = dayDifferenceBetweenRepeatingTasks * 24;
      }
    }
    return {
      valid: timeBeforeNextTask >= totalTime,
      timeBeforeNextTask,
      timeBeforeNextTaskDisplay: this.formatDuration(timeBeforeNextTask),
      totalTime,
    };
  }

  getMultiplier(type: string): number {
    switch (type) {
      case 'hours':
        return 1;
      case 'days':
        return 24;
      case 'weeks':
        return 24 * 7;
    }
  }

  findSmallestDayDifference(arr: number[]): number {
    arr.sort((a, b) => a - b);

    // Initialize the smallest difference with a large number.
    let smallestDifference = Infinity;

    // Iterate through the array to find the smallest difference between adjacent elements.
    for (let i = 1; i < arr.length; i++) {
      const difference = arr[i] - arr[i - 1];
      // Update the smallest difference if the current difference is smaller.
      if (difference < smallestDifference) {
        smallestDifference = difference;
      }
    }

    return smallestDifference;
  }

  findSmallestDayDifferenceWithWraparound(days: number[]): number {
    // Ensure the list is sorted to make comparison easier
    days.sort((a, b) => a - b);
    // Initialize the smallest difference to the maximum possible, 7 days
    let minDiff: number = 7;
    for (let i = 0; i < days.length - 1; i++) {
      // Direct difference
      const directDiff: number = days[i + 1] - days[i];
      // Difference that wraps around the end of the week
      const wrapAroundDiff: number = 7 - directDiff;
      // Update the smallest difference
      minDiff = Math.min(minDiff, directDiff, wrapAroundDiff);
    }
    // Special case for the first and last element, which might have a smaller wrap-around difference
    const firstLastDiff: number = Math.min(days[0] + 7 - days[days.length - 1], days[days.length - 1] + 7 - days[0]);
    minDiff = Math.min(minDiff, firstLastDiff);

    return minDiff;
  }

  formatDuration(hours: number): string {
    // Constants for conversion
    const hoursPerDay = 24;
    const daysPerWeek = 7;

    // Calculate the breakdown into weeks, days, and hours
    const weeks = Math.floor(hours / (hoursPerDay * daysPerWeek));
    const days = Math.floor((hours % (hoursPerDay * daysPerWeek)) / hoursPerDay);
    const remainingHours = hours % hoursPerDay;

    // Create an array of time components and their labels, excluding any with a value of 0
    const timeComponents = [
      {value: weeks, label: 'week'},
      {value: days, label: 'day'},
      {value: remainingHours, label: 'hour'},
    ]
      .filter(component => component.value !== 0)
      .map(component => `${component.value} ${component.label}${component.value > 1 ? 's' : ''}`);

    // Join the components with commas and return the result
    return timeComponents.join(', ') || '0 hours'; // Default to "0 hours" if all components are 0
  }

  checkIfTaskHasTrigger(task: ProjectTask): boolean {
    return task.triggers.some(trigger => trigger.trackerId && trigger.metricId);
  }

  checkIfTriggerIsComplete(trigger: TaskTrigger, metricType?: TrackerMetricTypes) {
    if (metricType === TrackerMetricTypes.Duration) {
      return !!trigger.value;
    }
    return trigger.metricId && !!trigger.value;
  }

  checkIfSubtaskIsComplete(subtask: Subtask): boolean {
    if (!subtask.trackerId) {
      return true;
    }
    return !!subtask.trackerId && !!subtask.metricIds.length;
  }

  findIconByKeyword(keyword: string): string {
    if (!keyword) return '';
    const lowerKeyword = keyword.toLowerCase();
    for (const category of TASK_ICON_PICKER_ICONS) {
      for (const icon of category.icons) {
        if (icon.keywords.some(k => k.toLowerCase().startsWith(lowerKeyword))) {
          return icon.name;
        }
      }
    }
    return '';
  }

  getImgArrayFromHTMLString(htmlString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const imgElements = doc.querySelectorAll('img');
    const srcs = Array.from(imgElements).map(img => img.src);
    return srcs;
  }
}
